import { is, getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import eventDefinitionHelper from "bpmn-js-properties-panel/lib/helper/EventDefinitionHelper";
import extensionsElementHelper from "bpmn-js-properties-panel/lib/helper/ExtensionElementsHelper";

const ImplementationTypeHelper = {};

/**
 * Returns 'true' if the given element is 'camunda:ServiceTaskLike'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isServiceTaskLike = function (element) {
  return is(element, "camunda:ServiceTaskLike");
};

/**
 * Returns 'true' if the given element is 'camunda:DmnCapable'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isDmnCapable = function (element) {
  return is(element, "camunda:DmnCapable");
};

/**
 * Returns 'true' if the given element is 'camunda:ExternalCapable'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isExternalCapable = function (element) {
  return is(element, "camunda:ExternalCapable");
};

/**
 * Returns 'true' if the given element is 'camunda:TaskListener'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isTaskListener = function (element) {
  return is(element, "camunda:TaskListener");
};

/**
 * Returns 'true' if the given element is 'camunda:ExecutionListener'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isExecutionListener = function (element) {
  return is(element, "camunda:ExecutionListener");
};

/**
 * Returns 'true' if the given element is 'camunda:ExecutionListener' or
 * 'camunda:TaskListener'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isListener = function (element) {
  return this.isTaskListener(element) || this.isExecutionListener(element);
};

/**
 * Returns 'true' if the given element is 'bpmn:SequenceFlow'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isSequenceFlow = function (element) {
  return is(element, "bpmn:SequenceFlow");
};

/**
 * Get a 'camunda:ServiceTaskLike' business object.
 *
 * If the given element is not a 'camunda:ServiceTaskLike', then 'false'
 * is returned.
 *
 * @param {djs.model.Base} element
 *
 * @return {ModdleElement} the 'camunda:ServiceTaskLike' business object
 */
ImplementationTypeHelper.getServiceTaskLikeBusinessObject = function (element) {
  if (
    is(element, "bpmn:IntermediateThrowEvent") ||
    is(element, "bpmn:EndEvent")
  ) {
    // change business object to 'messageEventDefinition' when
    // the element is a message intermediate throw event or message end event
    // because the camunda extensions (e.g. camunda:class) are in the message
    // event definition tag and not in the intermediate throw event or end event tag
    const messageEventDefinition =
      eventDefinitionHelper.getMessageEventDefinition(element);
    if (messageEventDefinition) {
      element = messageEventDefinition;
    }
  }

  return this.isServiceTaskLike(element) && getBusinessObject(element);
};

/**
 * Returns 'true' if the given element is 'bpmn:SendTask'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isRegistrarDocumentoSupportCapable = function (
  element,
) {
  return is(element, "bpmn:SendTask");
};

/**
 * Returns 'true' if the given element is 'bpmn:SendTask'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isGerarPdfRegistrarSupportCapable = function (
  element,
) {
  return is(element, "bpmn:SendTask"); //GeneratePdfAndRegister");
};

ImplementationTypeHelper.isGerarMinutaSupportCapable = function (element) {
  return is(element, "bpmn:SendTask");
};

/**
 * Returns 'true' if the given element is 'bpmn:ServiceTask'
 *
 * @param {djs.model.Base} element
 *
 * @return {boolean} a boolean value
 */
ImplementationTypeHelper.isRegistrarArquivarDocumentoSupportCapable = function (
  element,
) {
  return is(element, "bpmn:ServiceTask");
};

ImplementationTypeHelper.isGerarPdfNewSupportCapable = function (element) {
  return is(element, "bpmn:ServiceTask");
};

ImplementationTypeHelper.isIntegrarDoczSupportCapable = function (element) {
  return is(element, "bpmn:ServiceTask");
};

ImplementationTypeHelper.isIntegrarDoczEtiquetaSupportCapable = function (
  element,
) {
  return is(element, "bpmn:ServiceTask");
};

ImplementationTypeHelper.isIntegrarDoczSesiSupportCapable = function(element) {
  return is(element, 'bpmn:ServiceTask');
};

ImplementationTypeHelper.isIntegrarAssinaturaSupportCapable = function (
  element,
) {
  return is(element, "bpmn:ServiceTask");
};

ImplementationTypeHelper.isIntegrarAssinaturaLoteSupportCapable = function(element) {
  return is(element, 'bpmn:ServiceTask');
};

ImplementationTypeHelper.isIntegrarAssinaturaLoteGetResultSupportCapable = function(element) {
  return is(element, 'bpmn:ServiceTask');
};

ImplementationTypeHelper.isIntegrarApiManagerSupportCapable = function (
  element,
) {
  return is(element, "bpmn:ServiceTask");
};

ImplementationTypeHelper.isGerarQrBarCodeSupportCapable = function(element) {
  return is(element, 'bpmn:ServiceTask');
};

/**
 * Returns the implementation type of the given element.
 *
 * Possible implementation types are:
 * - dmn
 * - connector
 * - external
 * - class
 * - expression
 * - delegateExpression
 * - script
 *  -brms
 * - or undefined, when no matching implementation type is found
 *
 * @param  {djs.model.Base} element
 *
 * @return {String} the implementation type
 */
ImplementationTypeHelper.getImplementationType = function (element) {
  let bo = this.getServiceTaskLikeBusinessObject(element);

  if (!bo) {
    if (this.isListener(element)) {
      bo = element;
    } else {
      return;
    }
  }

  if (this.isDmnCapable(bo)) {
    const decisionRef = bo.get("camunda:decisionRef");
    if (typeof decisionRef !== "undefined") {
      return "dmn";
    }
    const type = bo.get("camunda:smartrules");
    if (typeof type !== "undefined" && type === "true") {
      return "smartrules";
    }
  }

  if (this.isServiceTaskLike(bo)) {
    const connectors = extensionsElementHelper.getExtensionElements(
      bo,
      "camunda:Connector",
    );
    if (typeof connectors !== "undefined") {
      return "connector";
    }
  }

  if (this.isExternalCapable(bo)) {
    const type = bo.get("camunda:type");
    if (type === "external") {
      return "external";
    }
  }

  if (this.isRegistrarDocumentoSupportCapable(bo)) {
    const type = bo.get("camunda:registrardocumento");
    if (typeof type !== "undefined" && type === "true") {
      return "registrardocumento";
    }
  }

  if (this.isGerarMinutaSupportCapable(bo)) {
    const type = bo.get("camunda:gerarminuta");
    if (typeof type !== "undefined" && type === "true") {
      return "gerarminuta";
    }
  }

  if (this.isGerarPdfRegistrarSupportCapable(bo)) {
    const type = bo.get("camunda:gerarpdfregistrar");
    if (typeof type !== "undefined" && type === "true") {
      return "gerarpdfregistrar";
    }
  }

  if (this.isRegistrarArquivarDocumentoSupportCapable(bo)) {
    const type = bo.get("camunda:registrararquivardocumento");
    if (typeof type !== "undefined" && type === "true") {
      return "registrararquivardocumento";
    }
  }

  if (this.isGerarPdfNewSupportCapable(bo)) {
    const type = bo.get("camunda:gerarpdfnew");
    if (typeof type !== "undefined" && type === "true") {
      return "gerarpdfnew";
    }
  }

  if (this.isIntegrarDoczSupportCapable(bo)) {
    const type = bo.get("camunda:integrardocz");
    if (typeof type !== "undefined" && type === "true") {
      return "integrardocz";
    }
  }

  if (this.isIntegrarDoczEtiquetaSupportCapable(bo)) {
    const type = bo.get("camunda:integrardoczetiqueta");
    if (typeof type !== "undefined" && type === "true") {
      return "integrardoczetiqueta";
    }
  }

  if (this.isIntegrarDoczSesiSupportCapable(bo)) {
    const type = bo.get("camunda:integrardoczsesi");
    if (typeof type !== "undefined" && type === "true") {
      return "integrardoczsesi";
    }
  }

  if (this.isIntegrarAssinaturaSupportCapable(bo)) {
    const type = bo.get("camunda:integrarassinatura");
    if (typeof type !== "undefined" && type === "true") {
      return "integrarassinatura";
    }
  }

  if (this.isIntegrarAssinaturaLoteSupportCapable(bo)) {
    const type = bo.get("camunda:integrarassinaturalote");
    if (typeof type !== "undefined" && type === "true") {
      return "integrarassinaturalote";
    }
  }

  if (this.isIntegrarAssinaturaLoteGetResultSupportCapable(bo)) {
    const type = bo.get("camunda:integrarassinaturalotegetresult");
    if (typeof type !== "undefined" && type === "true") {
      return "integrarassinaturalotegetresult";
    }
  }

  if (this.isIntegrarApiManagerSupportCapable(bo)) {
    const type = bo.get("camunda:integrarapimanager");
    if (typeof type !== "undefined" && type === "true") {
      return "integrarapimanager";
    }
  }

  if (this.isGerarQrBarCodeSupportCapable(bo)) {
    const type = bo.get("camunda:gerarqrbarcode");
    if (typeof type !== "undefined" && type === "true") {
      return "gerarqrbarcode";
    }
  }

  const cls = bo.get("camunda:class");
  if (typeof cls !== "undefined") {
    return "class";
  }

  const expression = bo.get("camunda:expression");
  if (typeof expression !== "undefined") {
    return "expression";
  }

  const delegateExpression = bo.get("camunda:delegateExpression");
  if (typeof delegateExpression !== "undefined") {
    return "delegateExpression";
  }

  if (this.isListener(bo)) {
    const script = bo.get("script");
    if (typeof script !== "undefined") {
      return "script";
    }
  }
};

export default ImplementationTypeHelper;
